// Der Stand des Druckerwächters aus dem Homelab-Protokoll (Topic
// `druckwaechter.stand`, docs/protokoll.md › „Druckerwächter über das
// Protokoll") in die Form, die die Ansicht aus der REST-Antwort kennt (WStand).
//
// Rein, ohne Runen und ohne Laufzeit-Importe, damit node (Test
// lib/druckwaechter-protokoll.test.mjs) sie laden kann: die Daten kommen schon
// skaliert (`skaliere("druckwaechter.stand", …)`), die Zeit-Umrechnung
// (`isoAusMs` aus protokoll/werte.ts) als Argument.

import type { DruckwaechterStand } from "../protokoll/schema.gen";
import type { Einstellung, Meinung, Sicht, Stelle, WStand } from "./waechter";

/** Unix-Millisekunden → ISO-Text (undefined, wenn die Zahl fehlt). */
export type IsoAusMs = (ms: unknown) => string | undefined;

function oder<T>(v: T | null | undefined): T | undefined {
  return v ?? undefined;
}

/** Skalierter Protokoll-Stand → WStand. Ein Stand ohne bekannten Zustand oder
 *  ohne Einstellung (nicht vollständig genug für die Ansicht) ergibt
 *  `undefined`: der Store behält dann den bisherigen. Fehlende Felder gelten
 *  so, wie die Ansicht sie aus der REST-Antwort kennt (Verlauf `[]`, Zahlen 0,
 *  Texte weg). */
export function wStandAusProtokoll(d: DruckwaechterStand, iso: IsoAusMs): WStand | undefined {
  if (!d.zustand || d.zustand === "unbekannt" || !d.einstellung) return undefined;
  const e = d.einstellung;
  const einstellung: Einstellung = {
    modus: e.modus ?? "pausieren",
    empfindlichkeit: e.empfindlichkeit ?? 0,
    schwelle_niedrig: e.schwelle_niedrig ?? 0,
    schwelle_hoch: e.schwelle_hoch ?? 0,
    steigerung: e.steigerung ?? 0,
    schonzeit_min: e.schonzeit_min ?? 0,
    ohne_meinung: e.ohne_meinung ?? false,
    modell: e.modell ?? "",
  };
  const s: WStand = {
    modus: d.modus ?? einstellung.modus,
    zustand: d.zustand,
    grund: oder(d.grund),
    seit: iso(d.seit) ?? "",
    datei: oder(d.datei),
    stufe: !d.stufe || d.stufe === "unbekannt" ? "ruhig" : d.stufe,
    wert: d.wert ?? 0,
    verlauf: d.verlauf ? [...d.verlauf] : [],
    schonzeit: iso(d.schonzeit),
    stumm: d.stumm ?? false,
    bilder_je_s: d.bilder_je_s ?? 0,
    erkenner_ms: d.erkenner_ms ?? 0,
    erkenner: oder(d.erkenner),
    takt_s: d.takt_s ?? 0,
    fragt: d.fragt ?? false,
    offener_fall: oder(d.offener_fall),
    letzter_fall: oder(d.letzter_fall),
    einstellung,
  };
  if (d.sicht) {
    const stellen: Stelle[] = (d.sicht.stellen ?? []).map((x) => ({
      p: x.p ?? 0,
      box: [x.x1 ?? 0, x.y1 ?? 0, x.x2 ?? 0, x.y2 ?? 0],
    }));
    const sicht: Sicht = {
      zeit: iso(d.sicht.zeit) ?? "",
      stellen,
      breite: d.sicht.breite ?? 0,
      hoehe: d.sicht.hoehe ?? 0,
      p: d.sicht.p ?? 0,
      bewertet: d.sicht.bewertet ?? false,
      hinweis: oder(d.sicht.hinweis),
    };
    s.sicht = sicht;
  }
  if (d.letzte_frage) {
    const m = d.letzte_frage;
    const frage: Meinung = {
      urteil: (m.urteil as Meinung["urteil"] | null | undefined) ?? "fehlt",
      art: oder(m.art) as Meinung["art"],
      begruendung: oder(m.begruendung),
      modell: m.modell ?? "",
      ms: m.ms ?? 0,
      fehler: oder(m.fehler),
      ausschnitt: m.ausschnitt || undefined,
    };
    s.letzte_frage = frage;
  }
  return s;
}
